import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaTrashAlt, FaUserSlash, FaLock } from 'react-icons/fa';
import { post } from '../service/api';

const KGemsDeleteAccount = () => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.userTokenReducer?.token);
  const [isAccountDeleting, setIsAccountDeleting] = useState(false);
  const [isDataDeleting, setIsDataDeleting] = useState(false);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('authToken');

    if (!isAuthenticated) {
      localStorage.setItem('redirectAfterLogin', '/kgems-delete-account');
    }
  }, []);

  const handleAccountDeleteRequest = async () => {
    try {
      setIsAccountDeleting(true);
      const response = await post('users/my-account-delete-request', {});
      if (response?.status) {
        toast.success(
          response?.message || 'Account deletion request sent successfully',
        );
      } else {
        toast.error(
          response?.message || 'Failed to send account deletion request',
        );
      }
    } catch (error) {
      console.error('Account deletion error:', error);
      toast.error('Error sending account deletion request');
    } finally {
      setIsAccountDeleting(false);
    }
  };

  const handleDataDeleteRequest = async () => {
    try {
      setIsDataDeleting(true);
      const response = await post('users/my-data-delete-request', {});
      if (response?.status) {
        toast.success(
          response?.message || 'Data deletion request sent successfully',
        );
      } else {
        toast.error(
          response?.message || 'Failed to send data deletion request',
        );
      }
    } catch (error) {
      console.error('Data deletion error:', error);
      toast.error('Error sending data deletion request');
    } finally {
      setIsDataDeleting(false);
    }
  };

  if (!token) {
    return (
      <div className="bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 min-h-screen font-sans">
        <div className="max-w-4xl mx-auto p-4">
          <div className="flex items-center justify-between border-b border-gray-700 pb-4 mb-6">
            <h1 className="text-yellow-400 text-2xl font-bold">Play Store</h1>
            <button
              onClick={() => navigate(-1)}
              className="text-gray-300 hover:text-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded-full p-2"
              aria-label="Go back"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          </div>

          <div className="bg-gray-800 rounded-xl p-8 shadow-2xl text-center">
            <FaLock className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-gray-100 text-2xl font-semibold mb-4">
              Authentication Required
            </h2>
            <p className="text-gray-400 mb-6">
              Please log in with your phone number and password to access this
              page. This ensures we verify your identity and provide a secure
              experience.
            </p>
            <button
              onClick={() => {
                localStorage.setItem('redirectAfterLogin', '/kgems-delete-account');
                navigate('/login');
              }}
              className="inline-block bg-yellow-400 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-500 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              aria-label="Log in to continue"
            >
              Log In Now
            </button>
          </div>
        </div>
        <ToastContainer position="top-left" autoClose={3000} />
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 min-h-screen font-sans">
      <div className="max-w-4xl mx-auto p-4">
        <div className="flex items-center justify-between border-b border-gray-700 pb-4 mb-6">
          <h1 className="text-yellow-400 text-2xl font-bold">
            Download Our App
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="text-gray-300 hover:text-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded-full p-2"
            aria-label="Go back"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        </div>

        <div className="bg-gray-800 rounded-xl p-8 shadow-2xl">
          <h2 className="text-gray-100 text-xl font-semibold mb-4">
            Get the Kolkata Gems App
          </h2>
          <p className="text-gray-400 mb-6">
            Download the Kolkata Gems mobile application from the Google Play
            Store to access our services anytime, anywhere. Enjoy a seamless
            experience with real-time updates and account management.
          </p>
          <a
            href="https://play.google.com/store/apps/details?id=com.KolkataGems"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-yellow-400 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-500 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 mb-8"
            aria-label="Download Kolkata Gems app from Play Store"
          >
            Download from Play Store
          </a>

          <h2 className="text-gray-100 text-xl font-semibold mb-4">
            Manage Your Account
          </h2>
          <p className="text-gray-400 mb-6">
            To protect your privacy, you can request the deletion of your
            account or personal data. Please note that these actions are
            permanent and will be processed in accordance with our privacy
            policy.
          </p>

          <div className="flex flex-col gap-4">
            <button
              onClick={handleAccountDeleteRequest}
              disabled={isAccountDeleting}
              className={`flex items-center gap-3 py-4 px-6 rounded-lg font-bold text-white bg-red-500 hover:bg-red-600 transition-colors duration-200 ${
                isAccountDeleting ? 'opacity-50 cursor-not-allowed' : ''
              } focus:outline-none focus:ring-2 focus:ring-red-400`}
              aria-label="Request account deletion"
            >
              <FaUserSlash className="w-6 h-6" />
              {isAccountDeleting ? 'Processing...' : 'Request Account Deletion'}
            </button>
            <button
              onClick={handleDataDeleteRequest}
              disabled={isDataDeleting}
              className={`flex items-center gap-3 py-4 px-6 rounded-lg font-bold text-white bg-red-500 hover:bg-red-600 transition-colors duration-200 ${
                isDataDeleting ? 'opacity-50 cursor-not-allowed' : ''
              } focus:outline-none focus:ring-2 focus:ring-red-400`}
              aria-label="Request data deletion"
            >
              <FaTrashAlt className="w-6 h-6" />
              {isDataDeleting ? 'Processing...' : 'Request Data Deletion'}
            </button>
          </div>
        </div>
      </div>
      <ToastContainer position="top-left" autoClose={3000} />
    </div>
  );
};

export default KGemsDeleteAccount;
